/**
 * ForecastCard Component
 *
 * Form card for editing forecast fields. Renders asset-specific fields
 * (contributions, growth) or liability-specific fields (interest rate, term, repayment).
 *
 * Props:
 *   - item: The asset/liability object
 *   - isAsset: Boolean indicating if this is an asset
 *   - isLiability: Boolean indicating if this is a liability
 *   - onFieldChange: Function(field, value) to update a field
 */

import CurrencyInput from "react-currency-input-field";

const intl = {style: "currency", locale: "en-GB", currency: "GBP", minimumFractionDigits: 0, maximumFractionDigits: 0};

const ForecastCard = ({item, isAsset, isLiability, onFieldChange}) => {
  return (
    <div className="card">
      <p className="regular">Forecast</p>

      <div className="form-group margin-top-medium">
        {/* ASSET FIELDS */}
        {isAsset && (
          <>
            <div className="form-item">
              <label htmlFor="annual_contributions">Annual contributions</label>
              <CurrencyInput id="annual_contributions" intlConfig={intl} className="number medium" defaultValue={0} value={item.annual_contributions} placeholder="'£10,000'" allowNegativeValue={false} allowDecimals={false} onValueChange={(value) => onFieldChange("annual_contributions", value)} />
            </div>
            <div className="form-item">
              <label htmlFor="annual_growth">Annual growth</label>
              <CurrencyInput
                id="annual_growth"
                suffix="%"
                className="number medium percent"
                defaultValue={0}
                placeholder="2.25%"
                value={item.annual_growth}
                allowNegativeValue={false}
                allowDecimals={true}
                decimalsLimit={2}
                decimalScale={2}
                maxLength={3}
                onValueChange={(value) => onFieldChange("annual_growth", value)}
              />
            </div>
          </>
        )}

        {/* LIABILITY FIELDS */}
        {isLiability && (
          <>
            <div className="form-item">
              <label htmlFor="interest_rate">Interest rate</label>
              <CurrencyInput
                id="interest_rate"
                suffix="%"
                className="number medium percent"
                defaultValue={0}
                placeholder="1.2%"
                value={item.interest_rate}
                allowNegativeValue={false}
                allowDecimals={true}
                decimalsLimit={2}
                decimalScale={2}
                maxLength={6}
                onValueChange={(value) => onFieldChange("interest_rate", value)}
              />
            </div>
            <div className="form-item term">
              <label htmlFor="term_years">Term</label>
            <div className="flex row center medium-gap">
                <CurrencyInput id="term_years" className="number medium term" placeholder="1" value={item.term_years} disableAbbreviations={true} allowNegativeValue={false} allowDecimals={false} maxLength={2} onValueChange={(value) => onFieldChange("term_years", value)} />
                <div className="medium"> {item.term_years === 1 ? " year" : " years"}</div>
                <CurrencyInput id="term_months" className="number medium term" placeholder="0" value={item.term_months} disableAbbreviations={true} allowDecimals={false} maxLength={2} onValueChange={(value) => {
                  const clamped = value !== undefined ? Math.min(Number(value), 11) : value;
                  onFieldChange("term_months", clamped);
                }} />
                <div className="medium"> {item.term_months === 1 ? " month" : "months"}</div>
           </div>
            </div>
            <div className="form-item">
              <label htmlFor="being_repaid">{item.classification === "Mortgage" ? "Interest only" : "Active repayments"}</label>
           
                <input
                  type="checkbox"
                  className="input-toggle"
                  checked={item.classification === "Mortgage" ? !item.being_repaid : item.being_repaid}
                  onChange={(e) => {
                    const val = item.classification === "Mortgage" ? !e.target.checked : e.target.checked;
                    onFieldChange("being_repaid", val);
                  }}
                />
           
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ForecastCard;
